<script setup>
import { computed, reactive, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import BaseInfo from 'dashboard/components-next/AssignmentPolicy/components/BaseInfo.vue';
import RadioCard from 'dashboard/components-next/radioCard/RadioCard.vue';
import FairDistribution from 'dashboard/components-next/AssignmentPolicy/components/FairDistribution.vue';
import DataTable from 'dashboard/components-next/AssignmentPolicy/components/DataTable.vue';
import AddDataDropdown from 'dashboard/components-next/AssignmentPolicy/components/AddDataDropdown.vue';
import DurationInput from 'dashboard/components-next/input/DurationInput.vue';
import { DURATION_UNITS } from 'dashboard/components-next/input/constants';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import PolicyFormSection from './PolicyFormSection.vue';
import PolicyFormFooter from './PolicyFormFooter.vue';
import Switch from 'dashboard/components-next/switch/Switch.vue';
import {
  OPTIONS,
  ROUND_ROBIN,
  EARLIEST_CREATED,
  DEFAULT_FAIR_DISTRIBUTION_LIMIT,
  DEFAULT_FAIR_DISTRIBUTION_WINDOW,
  DEFAULT_EXCLUDE_OLDER_THAN_HOURS,
} from 'dashboard/routes/dashboard/settings/assignmentPolicy/constants';

const props = defineProps({
  initialData: {
    type: Object,
    default: () => ({
      name: '',
      description: '',
      assignmentOrder: ROUND_ROBIN,
      conversationPriority: EARLIEST_CREATED,
      fairDistributionLimit: DEFAULT_FAIR_DISTRIBUTION_LIMIT,
      fairDistributionWindow: DEFAULT_FAIR_DISTRIBUTION_WINDOW,
      excludeOlderThanHours: DEFAULT_EXCLUDE_OLDER_THAN_HOURS,
      excludeAdministrators: false,
    }),
  },
  mode: {
    type: String,
    required: true,
    validator: value => ['CREATE', 'EDIT'].includes(value),
  },
  policyInboxes: {
    type: Array,
    default: () => [],
  },
  inboxList: {
    type: Array,
    default: () => [],
  },
  showInboxSection: {
    type: Boolean,
    default: false,
  },
  isLoading: {
    type: Boolean,
    default: false,
  },
  isInboxLoading: {
    type: Boolean,
    default: false,
  },
});
const emit = defineEmits([
  'submit',
  'addInbox',
  'deleteInbox',
  'navigateToInbox',
  'validationChange',
  'cancel',
]);
// Duration limits for the stale-conversation threshold: 1 hour to 999 days (in minutes)
const MIN_EXCLUSION_MINUTES = 60;
const MAX_EXCLUSION_MINUTES = 1438560;

const { t } = useI18n();

const BASE_KEY = 'ASSIGNMENT_POLICY.AGENT_ASSIGNMENT_POLICY';
const REDESIGN_KEY = 'ASSIGNMENT_POLICY.REDESIGN.ASSIGNMENT';
const COMMON_KEY = 'ASSIGNMENT_POLICY.REDESIGN.COMMON';

const baseInfoRef = ref(null);

const state = reactive({
  name: '',
  description: '',
  enabled: true,
  assignmentOrder: ROUND_ROBIN,
  conversationPriority: EARLIEST_CREATED,
  fairDistributionLimit: DEFAULT_FAIR_DISTRIBUTION_LIMIT,
  fairDistributionWindow: DEFAULT_FAIR_DISTRIBUTION_WINDOW,
  excludeOlderThanHours: DEFAULT_EXCLUDE_OLDER_THAN_HOURS,
  excludeAdministrators: false,
});

const validationState = ref({
  isValid: false,
});

const exclusionUnit = ref(DURATION_UNITS.DAYS);

// DurationInput works in minutes; the policy stores hours, so bridge the two
const excludeOlderThanMinutes = computed({
  get() {
    return state.excludeOlderThanHours == null
      ? null
      : state.excludeOlderThanHours * 60;
  },
  set(minutes) {
    state.excludeOlderThanHours =
      minutes == null ? null : Math.round(minutes / 60);
  },
});

const createOption = (
  type,
  key,
  stateKey,
  disabled = false,
  disabledMessage = '',
  disabledLabel = ''
) => ({
  key,
  label: t(`${BASE_KEY}.FORM.${type}.${key.toUpperCase()}.LABEL`),
  description: t(`${BASE_KEY}.FORM.${type}.${key.toUpperCase()}.DESCRIPTION`),
  isActive: state[stateKey] === key,
  disabled,
  disabledMessage,
  disabledLabel,
});

const assignmentOrderOptions = computed(() =>
  OPTIONS.ORDER.map(key =>
    createOption('ASSIGNMENT_ORDER', key, 'assignmentOrder')
  )
);

const assignmentPriorityOptions = computed(() =>
  OPTIONS.PRIORITY.map(key =>
    createOption('ASSIGNMENT_PRIORITY', key, 'conversationPriority')
  )
);

const radioSections = computed(() => [
  {
    key: 'assignmentOrder',
    icon: 'i-hugeicons-user-switch',
    title: t(`${REDESIGN_KEY}.SECTIONS.ORDER.TITLE`),
    description: t(`${REDESIGN_KEY}.SECTIONS.ORDER.DESCRIPTION`),
    label: t(`${BASE_KEY}.FORM.ASSIGNMENT_ORDER.LABEL`),
    options: assignmentOrderOptions.value,
  },
  {
    key: 'conversationPriority',
    icon: 'i-hugeicons-sorting-05',
    title: t(`${REDESIGN_KEY}.SECTIONS.PRIORITY.TITLE`),
    description: t(`${REDESIGN_KEY}.SECTIONS.PRIORITY.DESCRIPTION`),
    label: t(`${BASE_KEY}.FORM.ASSIGNMENT_PRIORITY.LABEL`),
    options: assignmentPriorityOptions.value,
  },
]);

const footerHint = computed(() =>
  t(
    `${COMMON_KEY}.${validationState.value.isValid ? 'READY_HINT' : 'REQUIRED_HINT'}`
  )
);

const buttonLabel = computed(() =>
  t(`${BASE_KEY}.${props.mode.toUpperCase()}.${props.mode}_BUTTON`)
);

const handleValidationChange = validation => {
  validationState.value = validation;
  emit('validationChange', validation);
};

const resetForm = () => {
  Object.assign(state, {
    name: '',
    description: '',
    enabled: true,
    assignmentOrder: ROUND_ROBIN,
    conversationPriority: EARLIEST_CREATED,
    fairDistributionLimit: DEFAULT_FAIR_DISTRIBUTION_LIMIT,
    fairDistributionWindow: DEFAULT_FAIR_DISTRIBUTION_WINDOW,
    excludeOlderThanHours: DEFAULT_EXCLUDE_OLDER_THAN_HOURS,
    excludeAdministrators: false,
  });
  baseInfoRef.value?.resetValidation();
};

const handleSubmit = () => {
  if (!validationState.value.isValid) {
    baseInfoRef.value?.touchAll();
    return;
  }
  emit('submit', { ...state });
};

// Pick the display unit from the stored value so non-day thresholds (e.g. 25h) don't get floored
const detectExclusionUnit = hours => {
  exclusionUnit.value =
    hours && hours % 24 !== 0 ? DURATION_UNITS.HOURS : DURATION_UNITS.DAYS;
};

watch(
  () => props.initialData,
  newData => {
    Object.assign(state, newData);
    detectExclusionUnit(newData.excludeOlderThanHours);
  },
  { immediate: true, deep: true }
);

defineExpose({
  resetForm,
});
</script>

<template>
  <form class="flex flex-col gap-4" novalidate @submit.prevent="handleSubmit">
    <div class="grid grid-cols-1 items-start gap-4 xl:grid-cols-2">
      <div class="flex min-w-0 flex-col gap-4">
        <PolicyFormSection
          icon="i-hugeicons-file-edit"
          :title="t(`${REDESIGN_KEY}.SECTIONS.IDENTITY.TITLE`)"
          :description="t(`${REDESIGN_KEY}.SECTIONS.IDENTITY.DESCRIPTION`)"
        >
          <BaseInfo
            ref="baseInfoRef"
            v-model:policy-name="state.name"
            v-model:description="state.description"
            :name-label="t(`${BASE_KEY}.FORM.NAME.LABEL`)"
            :name-placeholder="t(`${BASE_KEY}.FORM.NAME.PLACEHOLDER`)"
            :description-label="t(`${BASE_KEY}.FORM.DESCRIPTION.LABEL`)"
            :description-placeholder="
              t(`${BASE_KEY}.FORM.DESCRIPTION.PLACEHOLDER`)
            "
            :name-error-message="t(`${COMMON_KEY}.NAME_REQUIRED`)"
            :description-error-message="t(`${COMMON_KEY}.DESCRIPTION_REQUIRED`)"
            @validation-change="handleValidationChange"
          />
        </PolicyFormSection>

        <PolicyFormSection
          icon="i-hugeicons-balance-scale"
          :title="t(`${REDESIGN_KEY}.SECTIONS.FAIR.TITLE`)"
          :description="t(`${BASE_KEY}.FORM.FAIR_DISTRIBUTION.DESCRIPTION`)"
        >
          <FairDistribution
            v-model:fair-distribution-limit="state.fairDistributionLimit"
            v-model:fair-distribution-window="state.fairDistributionWindow"
            v-model:window-unit="state.windowUnit"
          />
        </PolicyFormSection>

        <PolicyFormSection
          icon="i-hugeicons-filter"
          :title="t(`${REDESIGN_KEY}.SECTIONS.RULES.TITLE`)"
          :description="t(`${REDESIGN_KEY}.SECTIONS.RULES.DESCRIPTION`)"
        >
          <div
            class="flex items-center justify-between gap-4 rounded-lg border border-n-weak p-3"
          >
            <div class="flex min-w-0 flex-col items-start gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t(`${BASE_KEY}.FORM.EXCLUDE_ADMINISTRATORS.LABEL`) }}
              </span>
              <p class="mb-0 text-sm text-n-slate-11">
                {{ t(`${BASE_KEY}.FORM.EXCLUDE_ADMINISTRATORS.DESCRIPTION`) }}
              </p>
            </div>
            <Switch
              v-model="state.excludeAdministrators"
              class="shrink-0"
              :aria-label="t(`${BASE_KEY}.FORM.EXCLUDE_ADMINISTRATORS.LABEL`)"
            />
          </div>

          <div class="flex flex-col gap-2 rounded-lg border border-n-weak p-3">
            <div class="flex flex-col items-start gap-1">
              <span class="text-sm font-medium text-n-slate-12">
                {{ t(`${BASE_KEY}.FORM.EXCLUDE_OLDER_THAN.LABEL`) }}
              </span>
              <p class="mb-0 text-sm text-n-slate-11">
                {{ t(`${BASE_KEY}.FORM.EXCLUDE_OLDER_THAN.DESCRIPTION`) }}
              </p>
            </div>
            <div
              class="flex items-center gap-2 sm:max-w-sm [&>select]:!bg-n-alpha-2 [&>select]:!outline-none [&>select]:hover:brightness-110"
            >
              <DurationInput
                v-model:unit="exclusionUnit"
                v-model:model-value="excludeOlderThanMinutes"
                :min="MIN_EXCLUSION_MINUTES"
                :max="MAX_EXCLUSION_MINUTES"
              />
            </div>
          </div>
        </PolicyFormSection>
      </div>

      <div class="flex min-w-0 flex-col gap-4">
        <PolicyFormSection
          v-for="section in radioSections"
          :key="section.key"
          :icon="section.icon"
          :title="section.title"
          :description="section.description"
        >
          <div
            role="radiogroup"
            :aria-label="section.label"
            class="grid grid-cols-1 gap-3 sm:grid-cols-2"
          >
            <RadioCard
              v-for="option in section.options"
              :id="option.key"
              :key="option.key"
              :label="option.label"
              :description="option.description"
              :is-active="option.isActive"
              :disabled="option.disabled"
              :disabled-label="option.disabledLabel"
              :disabled-message="option.disabledMessage"
              @select="state[section.key] = $event"
            />
          </div>
        </PolicyFormSection>

        <aside
          v-if="!showInboxSection"
          class="rounded-xl border border-dashed border-n-weak p-4 sm:p-5"
        >
          <h2
            class="mb-2 flex items-center gap-2 text-sm font-semibold text-n-slate-12"
          >
            <Icon icon="i-hugeicons-idea-01" class="size-4 text-n-slate-11" />
            {{ t(`${COMMON_KEY}.NEXT_STEPS`) }}
          </h2>
          <ol class="mb-0 flex list-decimal flex-col gap-1.5 ps-5 text-sm">
            <li class="text-n-slate-11">
              {{ t(`${REDESIGN_KEY}.NEXT_STEP_INBOXES`) }}
            </li>
            <li class="text-n-slate-11">
              {{ t(`${REDESIGN_KEY}.NEXT_STEP_EDIT`) }}
            </li>
          </ol>
        </aside>
      </div>
    </div>

    <PolicyFormSection
      v-if="showInboxSection"
      icon="i-hugeicons-inbox"
      :title="t(`${REDESIGN_KEY}.SECTIONS.INBOXES.TITLE`)"
      :description="t(`${REDESIGN_KEY}.SECTIONS.INBOXES.DESCRIPTION`)"
    >
      <template #actions>
        <AddDataDropdown
          :label="t(`${BASE_KEY}.FORM.INBOXES.ADD_BUTTON`)"
          :search-placeholder="
            t(`${BASE_KEY}.FORM.INBOXES.DROPDOWN.SEARCH_PLACEHOLDER`)
          "
          :items="inboxList"
          @add="$emit('addInbox', $event)"
        />
      </template>
      <p class="-mt-1 mb-0 text-xs text-n-slate-11">
        {{ t(`${COMMON_KEY}.IMMEDIATE_SAVE`) }}
      </p>
      <DataTable
        :items="policyInboxes"
        :is-fetching="isInboxLoading"
        :empty-state-message="t(`${BASE_KEY}.FORM.INBOXES.EMPTY_STATE`)"
        @delete="$emit('deleteInbox', $event)"
        @navigate="$emit('navigateToInbox', $event)"
      />
    </PolicyFormSection>

    <PolicyFormFooter
      :submit-label="buttonLabel"
      :cancel-label="t(`${COMMON_KEY}.CANCEL`)"
      :hint="footerHint"
      :is-valid="validationState.isValid"
      :is-loading="isLoading"
      @cancel="$emit('cancel')"
    />
  </form>
</template>
